import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Package, RefreshCw, CreditCard, Clock } from "lucide-react";

const ReturnsPage = () => {
  return (
    <div className="min-h-screen bg-background animate-fade-in" data-testid="returns-page">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 py-16">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-zinc-400 hover:text-white transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight" data-testid="page-title">
            Returns & Refunds Policy
          </h1>
          <p className="text-zinc-400 mt-4">Last updated: January 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Quick Overview */}
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
              <Clock className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-white font-bold">30 Days</p>
              <p className="text-zinc-400 text-sm">Return Window</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
              <Package className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-white font-bold">Original Condition</p>
              <p className="text-zinc-400 text-sm">Required</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
              <RefreshCw className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-white font-bold">7 Business Days</p>
              <p className="text-zinc-400 text-sm">Refund Processing</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
              <CreditCard className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-white font-bold">3 Options</p>
              <p className="text-zinc-400 text-sm">Refund Choices</p>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-invert max-w-none space-y-8">
            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Return Eligibility</h2>
              <p className="text-zinc-400 mb-4">
                We want you to be completely satisfied with your purchase. If you're not happy with your order, 
                you may return eligible items within <strong className="text-white">30 days from the delivery date</strong>.
              </p>
              <h3 className="text-lg font-semibold text-white mt-6 mb-3">Items Must Be:</h3>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>In original, unused condition</li>
                <li>In original packaging with all tags and labels intact</li>
                <li>Accompanied by the original receipt or proof of purchase</li>
                <li>Free from installation marks, scratches, or damage</li>
              </ul>
              <h3 className="text-lg font-semibold text-white mt-6 mb-3">Non-Returnable Items:</h3>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Electrical components that have been installed or tested</li>
                <li>Custom-ordered or special-order parts</li>
                <li>Items marked as "Final Sale" or "Clearance"</li>
                <li>Hazardous materials (fluids, chemicals)</li>
              </ul>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Return Shipping</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-zinc-950 p-6 border border-zinc-700">
                  <h3 className="text-primary font-bold mb-2">Defective Items</h3>
                  <p className="text-zinc-400">
                    If you received a defective or damaged item, <strong className="text-white">Brendolly's will cover all return shipping costs</strong>. 
                    Contact us within 48 hours of delivery with photos of the damage.
                  </p>
                </div>
                <div className="bg-zinc-950 p-6 border border-zinc-700">
                  <h3 className="text-secondary font-bold mb-2">Customer-Initiated Returns</h3>
                  <p className="text-zinc-400">
                    For returns due to change of mind or incorrect ordering, <strong className="text-white">the customer is responsible for return shipping costs</strong>. 
                    We recommend using a trackable shipping service.
                  </p>
                </div>
              </div>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Refund Options</h2>
              <p className="text-zinc-400 mb-4">
                Once we receive and inspect your return, you will be notified of the approval or rejection within 3 business days. 
                Approved refunds will be processed within <strong className="text-white">7 business days</strong>.
              </p>
              <div className="grid md:grid-cols-3 gap-4 mt-6">
                <div className="bg-zinc-950 p-6 border border-primary text-center">
                  <h3 className="text-white font-bold mb-2">Full Refund</h3>
                  <p className="text-zinc-400 text-sm">Original payment method</p>
                </div>
                <div className="bg-zinc-950 p-6 border border-zinc-700 text-center">
                  <h3 className="text-white font-bold mb-2">Exchange</h3>
                  <p className="text-zinc-400 text-sm">Same or different item</p>
                </div>
                <div className="bg-zinc-950 p-6 border border-zinc-700 text-center">
                  <h3 className="text-white font-bold mb-2">Store Credit</h3>
                  <p className="text-zinc-400 text-sm">Never expires</p>
                </div>
              </div>
              <p className="text-zinc-500 text-sm mt-6">
                Note: Original shipping charges are non-refundable unless the return is due to our error.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">How to Initiate a Return</h2>
              <ol className="list-decimal list-inside text-zinc-400 space-y-3">
                <li><strong className="text-white">Contact Us:</strong> Email contact@brendollysautoparts.com or call 781-870-7976 with your order number</li>
                <li><strong className="text-white">Receive RMA:</strong> We'll provide a Return Merchandise Authorization (RMA) number</li>
                <li><strong className="text-white">Pack Securely:</strong> Place items in original packaging with RMA number visible</li>
                <li><strong className="text-white">Ship:</strong> Send to our returns center using a trackable shipping method</li>
                <li><strong className="text-white">Confirmation:</strong> Receive email confirmation once we process your return</li>
              </ol>
            </section>

            <section className="bg-primary/10 border border-primary p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">Questions?</h2>
              <p className="text-zinc-300">
                Our customer support team is here to help with any questions about returns or refunds.
              </p>
              <div className="flex flex-wrap gap-4 mt-4">
                <Link to="/contact" className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider px-6 py-3 inline-block">
                  Contact Us
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnsPage;
